import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  firstValueFrom,
  map,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import { ActionResponse, AuthResponse, LoginRequest, RegisterRequest } from '../models/auth.models';
import { UserProfile } from '../models/user.models';
import { TokenStorageService } from './token-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenStore = inject(TokenStorageService);

  private readonly userSubject = new BehaviorSubject<UserProfile | null>(null);
  readonly user$ = this.userSubject.asObservable();
  private bootstrapPromise: Promise<void> | null = null;

  login(payload: LoginRequest): Observable<UserProfile> {
    return this.http.post<AuthResponse>(`${API_BASE_URL}/auth/login`, payload).pipe(
      tap((response) => {
        if (!response.accessToken) {
          throw new Error(response.message ?? 'Connexion impossible.');
        }
        this.tokenStore.saveToken(response.accessToken);
      }),
      switchMap(() => this.http.get<UserProfile>(`${API_BASE_URL}/users/me`)),
      tap((user) => this.userSubject.next(user)),
      catchError((error) => {
        this.tokenStore.clear();
        this.userSubject.next(null);
        return throwError(() => error);
      })
    );
  }

  register(payload: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${API_BASE_URL}/auth/register`, payload).pipe(
      switchMap((response) => {
        const token = response.accessToken?.trim();
        if (!token) {
          return of(response);
        }

        this.tokenStore.saveToken(token);
        return this.http.get<UserProfile>(`${API_BASE_URL}/users/me`).pipe(
          tap((user) => this.userSubject.next(user)),
          map(() => response)
        );
      }),
      catchError((error) => {
        this.tokenStore.clear();
        this.userSubject.next(null);
        return throwError(() => error);
      })
    );
  }

  bootstrapSession(forceAnonymous = false): Promise<void> {
    if (!forceAnonymous && this.bootstrapPromise) {
      return this.bootstrapPromise;
    }

    const bootstrap$ = forceAnonymous || !this.tokenStore.getToken()
      ? this.createAnonymousSession()
      : this.refreshProfileOrCreateAnonymous();

    this.bootstrapPromise = firstValueFrom(bootstrap$).finally(() => {
      this.bootstrapPromise = null;
    });

    return this.bootstrapPromise;
  }

  requestPasswordReset(email: string): Observable<ActionResponse> {
    return this.http.post<ActionResponse>(`${API_BASE_URL}/auth/password/forgot`, { email });
  }

  validatePasswordResetToken(token: string): Observable<boolean> {
    return this.http
      .get<{ valid?: boolean }>(`${API_BASE_URL}/auth/password/reset/validate`, {
        params: { token },
      })
      .pipe(map((response) => response.valid === true));
  }

  resetPassword(token: string, password: string): Observable<ActionResponse> {
    return this.http.post<ActionResponse>(`${API_BASE_URL}/auth/password/reset`, {
      token,
      password,
    });
  }

  resendEmailVerification(email: string): Observable<ActionResponse> {
    return this.http.post<ActionResponse>(`${API_BASE_URL}/auth/verify-email/resend`, { email });
  }

  confirmEmailVerification(token: string): Observable<ActionResponse> {
    return this.http.post<ActionResponse>(`${API_BASE_URL}/auth/verify-email/confirm`, { token });
  }

  refreshProfile(): Observable<UserProfile | null> {
    if (!this.hasSession()) {
      this.userSubject.next(null);
      return of(null);
    }

    return this.http.get<UserProfile>(`${API_BASE_URL}/users/me`).pipe(
      tap((user) => this.userSubject.next(user)),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          this.logout();
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    this.tokenStore.clear();
    this.userSubject.next(null);
  }

  async logoutToAnonymous(): Promise<void> {
    this.logout();
    await this.bootstrapSession(true);
  }

  hasSession(): boolean {
    return !!this.tokenStore.getToken();
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedNonAnonymous();
  }

  isAuthenticatedNonAnonymous(): boolean {
    return this.hasSession() && !this.isAnonymousProfile(this.currentUser);
  }

  getToken(): string | null {
    return this.tokenStore.getToken();
  }

  get currentUser(): UserProfile | null {
    return this.userSubject.value;
  }

  isAnonymousProfile(user: UserProfile | null | undefined): boolean {
    const email = user?.email?.trim().toLowerCase();
    return !!email && email.endsWith('@anonymous.com');
  }

  private refreshProfileOrCreateAnonymous(): Observable<void> {
    return this.refreshProfile().pipe(
      switchMap((user) => {
        if (user) {
          return of(void 0);
        }
        return this.createAnonymousSession();
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          this.logout();
          return this.createAnonymousSession();
        }
        this.userSubject.next(null);
        return of(void 0);
      })
    );
  }

  private createAnonymousSession(): Observable<void> {
    return this.http.post<AuthResponse>(`${API_BASE_URL}/auth/anonymous`, {}).pipe(
      switchMap((response) => {
        const token = response.accessToken?.trim();
        if (!token) {
          this.logout();
          return of(null);
        }

        this.tokenStore.saveToken(token);
        return this.http.get<UserProfile>(`${API_BASE_URL}/users/me`);
      }),
      tap((user) => this.userSubject.next(user)),
      map(() => void 0),
      catchError(() => {
        this.logout();
        return of(void 0);
      })
    );
  }
}
